import type { AdminIntegrationRow } from '../../../domain/superadmin/types';
import {
    integrationStatusLabel,
    integrationTypeLabel,
} from '../../../domain/superadmin/integrationFilters';
import { formatDate } from '../../../utils/formatters';
import { cn } from '@/lib/utils';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';
import { SectionEmpty } from '../organizations/sections/SectionEmpty';

interface IntegrationsTableProps {
    items: AdminIntegrationRow[];
}

function StatusBadge({ status }: { status: string | null }) {
    const connected = status === 'connected';
    return (
        <span
            className={cn(
                'inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium',
                connected
                    ? 'border-success/30 bg-success-bg text-success-text'
                    : 'border-error/40 bg-danger-bg text-danger-text',
            )}
        >
            {integrationStatusLabel(status)}
        </span>
    );
}

function ExpiresCell({ row }: { row: AdminIntegrationRow }) {
    if (!row.expiresAt) return <span className="text-muted">—</span>;

    return (
        <span className="inline-flex flex-wrap items-center gap-s2">
            <span className="font-mono text-[13px] tabular-nums text-muted">
                {formatDate(row.expiresAt)}
            </span>
            {row.expiringSoon && (
                <span className="inline-flex rounded-full border border-warning/30 bg-warning-bg px-2 py-0.5 text-[11px] font-medium text-warning-text">
                    ≤7d
                </span>
            )}
        </span>
    );
}

function IntegrationCard({ row }: { row: AdminIntegrationRow }) {
    return (
        <article className="rounded-xl border border-subtle bg-card p-s4 shadow-sm">
            <header className="mb-s3 flex items-start justify-between gap-s2">
                <div className="min-w-0">
                    <h3 className="truncate text-sm font-semibold text-default">
                        {row.organizationName}
                    </h3>
                    <p className="truncate text-[13px] text-muted">{row.name || '—'}</p>
                </div>
                <StatusBadge status={row.status} />
            </header>
            <dl className="grid grid-cols-2 gap-s2 text-[13px]">
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                        Tipo
                    </dt>
                    <dd className="text-secondary">{integrationTypeLabel(row.type)}</dd>
                </div>
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                        Account ID
                    </dt>
                    <dd className="truncate font-mono tabular-nums text-default">
                        {row.accountId || '—'}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                        Expira em
                    </dt>
                    <dd>
                        <ExpiresCell row={row} />
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                        Criada em
                    </dt>
                    <dd className="font-mono tabular-nums text-muted">
                        {row.createdAt ? formatDate(row.createdAt) : '—'}
                    </dd>
                </div>
            </dl>
        </article>
    );
}

export function IntegrationsTable({ items }: IntegrationsTableProps) {
    if (items.length === 0) {
        return <SectionEmpty message="Nenhuma integração encontrada." />;
    }

    return (
        <section aria-label="Integrações">
            <ul className="grid list-none gap-s3 p-0 md:hidden">
                {items.map((row) => (
                    <li key={row.id}>
                        <IntegrationCard row={row} />
                    </li>
                ))}
            </ul>

            <div className="hidden overflow-x-auto md:block">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Empresas</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Account ID</TableHead>
                            <TableHead>Pixel</TableHead>
                            <TableHead>Página</TableHead>
                            <TableHead>Expira em</TableHead>
                            <TableHead>Criada em</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <span className="font-medium text-default">
                                        {row.organizationName}
                                    </span>
                                </TableCell>
                                <TableCell className="text-secondary">
                                    {integrationTypeLabel(row.type)}
                                </TableCell>
                                <TableCell className="text-muted">{row.name || '—'}</TableCell>
                                <TableCell>
                                    <StatusBadge status={row.status} />
                                </TableCell>
                                <TableCell className="font-mono text-[13px] tabular-nums text-muted">
                                    {row.accountId || '—'}
                                </TableCell>
                                <TableCell className="text-muted">{row.pixelName || '—'}</TableCell>
                                <TableCell className="text-muted">{row.pageName || '—'}</TableCell>
                                <TableCell>
                                    <ExpiresCell row={row} />
                                </TableCell>
                                <TableCell className="font-mono text-[13px] tabular-nums text-muted">
                                    {row.createdAt ? formatDate(row.createdAt) : '—'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </section>
    );
}
